// Executive-cockpit dashboard view.
// Inspiration: morgen.so + structured.app for the schedule timeblock,
// dense Bloomberg-terminal-style data layout for everything else.
//
// Layout = grid: [nav-rail | main canvas | collapsible chat rail]
// Top of canvas: integrations strip → day-at-glance → CEO KPIs → hero
// Then view-specific content based on the active view tab.

import type { User } from "@supabase/supabase-js";
import { ICONS } from "./icons";
import {
  KPIS, HERO, TODOS, LIVE, SCHEDULE, TRIAGE, PIPELINE,
  AWAITING, REGULATORY, CHAT_HISTORY, MESSAGES_PREVIEW,
  NOTES, REMINDERS, HOLDINGS, PORTFOLIO_SUMMARY, INVEST_NEWS,
  PERSONAL, STAFF_PULSE, DECISIONS, NEXT_UP, PATIENTS_TODAY,
  PEPTIDE_CYCLE, PEPTIDE_TODAY, PEPTIDE_WEEK,
  DAY_START_HOUR, DAY_END_HOUR,
  type Todo, type ScheduleItem,
  type AppleReminder, type AppleNote,
} from "./placeholders";
import { supabase } from "./supabase";

const STORAGE_RAIL = "alfred.rail.state";
const STORAGE_VIEW = "alfred.view";
const STORAGE_CRUD = "alfred.crud.v1";
const STORAGE_RNTAB = "alfred.rnTab";
const STORAGE_DECISIONS = "alfred.decisions.v1";
const STORAGE_LASTSEEN = "alfred.lastSeen";

// ── Toast (lightweight notifications) ───────────────────────────────────
function ensureToastHost(): HTMLElement {
  let host = document.getElementById("toast-host");
  if (!host) {
    host = document.createElement("div");
    host.id = "toast-host";
    host.className = "toast-host";
    document.body.appendChild(host);
  }
  return host;
}
function toast(msg: string, kind: "ok" | "warn" | "error" | "" = "", ms = 3200) {
  const host = ensureToastHost();
  const el = document.createElement("div");
  el.className = "toast " + (kind || "");
  el.textContent = msg;
  host.appendChild(el);
  setTimeout(() => { el.style.opacity = "0"; el.style.transform = "translateY(-10px)"; }, ms - 250);
  setTimeout(() => el.remove(), ms);
}

// ── Decision-state store (approved/edited/deferred) ─────────────────────
type DecisionResult = "approved" | "edited" | "deferred";
type DecisionStore = Record<string, { result: DecisionResult; label: string; at: string }>;
function loadDecisions(): DecisionStore {
  return loadJson<DecisionStore>(STORAGE_DECISIONS, {});
}
function saveDecisions(s: DecisionStore) { saveJson(STORAGE_DECISIONS, s); }

// ── CRUD store for Reminders / Todos / Notes ───────────────────────────
type CrudState = {
  reminders: (AppleReminder & { id: string })[];
  todos: Todo[];
  notes: (AppleNote & { id: string })[];
};

function defaultCrudState(): CrudState {
  return {
    reminders: REMINDERS.map((r, i) => ({ ...r, id: "r" + i })),
    todos: TODOS.map(t => ({ ...t })),
    notes: NOTES.map((n, i) => ({ ...n, id: "n" + i })),
  };
}
let crudState: CrudState = defaultCrudState();
function loadCrud() {
  const saved = loadJson<CrudState | null>(STORAGE_CRUD, null);
  if (saved && saved.reminders && saved.todos && saved.notes) crudState = saved;
}
function saveCrud() { saveJson(STORAGE_CRUD, crudState); }

type View = "today" | "biogenix" | "personal" | "investments" | "inbox" | "calendar" | "patients";

function loadJson<T>(key: string, fallback: T): T {
  try { return JSON.parse(localStorage.getItem(key) || "") as T; }
  catch { return fallback; }
}
function saveJson(key: string, val: unknown) { localStorage.setItem(key, JSON.stringify(val)); }

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/\n/g, "<br>");
}

function todayStr(): string {
  return new Date().toLocaleDateString("en-AU", {
    weekday: "long", day: "numeric", month: "long",
  });
}
function timeAgo(mins: number): string {
  const d = new Date(Date.now() - mins * 60_000);
  return d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
}

// ── CEO headline (narrative summary of today) ───────────────────────────
function renderHeadline(): string {
  return `
    <div class="headline">
      Good morning, <em>Dominik</em>.
      Today: <span class="num">${DECISIONS.length}</span> decisions need you,
      <span class="num">${PATIENTS_TODAY.length}</span> patients booked,
      <span class="num">1</span> family commitment,
      <em>$${"284"}k</em> revenue MTD.
    </div>`;
}

// ── NEXT UP card ────────────────────────────────────────────────────────
function renderNextUp(): string {
  return `
    <div class="nextup">
      <div>
        <div class="nextup-eyebrow"><span class="pulse"></span> Next up</div>
        <div class="nextup-title">${escapeHtml(NEXT_UP.title)}</div>
        <div class="nextup-kind">${escapeHtml(NEXT_UP.kind)} · ${escapeHtml(NEXT_UP.duration)}</div>
        <div class="nextup-brief">${escapeHtml(NEXT_UP.brief)}</div>
      </div>
      <div class="nextup-actions">
        <div class="nu-time">${escapeHtml(NEXT_UP.startTime)}</div>
        <div class="nu-countdown">in ${NEXT_UP.inMinutes} min</div>
        <div class="nu-buttons">
          ${NEXT_UP.prepActions.map((a, i) =>
            `<button class="nu-btn ${i === 0 ? "" : "ghost"}">${escapeHtml(a.label)}</button>`).join("")}
        </div>
      </div>
    </div>`;
}

// ── DECISION QUEUE ──────────────────────────────────────────────────────
function renderDecisions(): string {
  const store = loadDecisions();
  const open = DECISIONS.filter(d => !store[d.id]);
  const resolved = DECISIONS.filter(d => store[d.id]);
  const list = [...open, ...resolved];
  return `
    <div class="section-head">
      <div>
        <div class="section-title">Decisions for <em>you</em></div>
        <div class="section-sub">${open.length} awaiting · approve / edit / defer</div>
      </div>
      ${resolved.length ? `<button class="section-link" id="reset-decisions">Reset all ${ICONS.arrowRight}</button>` : ""}
    </div>
    <div class="decisions" id="decisions-grid">
      ${list.map(d => renderDecisionCard(d, store)).join("")}
    </div>`;
}

function renderDecisionCard(d: typeof DECISIONS[number], store: DecisionStore): string {
  const resolved = store[d.id];
  const urgLabel = d.urgency === "now" ? "needs you now"
                 : d.urgency === "today" ? "today" : "this week";
  const urgCls = d.urgency === "now" ? "urg-now"
               : d.urgency === "today" ? "urg-today" : "urg-week";
  const resultCls = resolved ? "resolved " + resolved.result : "";
  return `
    <div class="decision ${d.urgency} ${resultCls}" data-decision="${d.id}" draggable="true">
      <div class="decision-head">
        <span class="${urgCls}">● ${urgLabel}</span>
        <span style="margin-left:auto;color:var(--slate-faint);">${escapeHtml(d.due)}</span>
      </div>
      <div class="decision-title">${escapeHtml(d.title)}</div>
      <div class="decision-context">${escapeHtml(d.context)}</div>
      <div class="decision-source">${escapeHtml(d.source)}</div>
      <div class="decision-result">${resolved ? `${escapeHtml(resolved.label)} · ${escapeHtml(resolved.at)}` : ""}</div>
      <div class="decision-actions">
        ${d.actions.map(a => `<button class="dec-btn ${a.kind}" data-dec-id="${d.id}" data-dec-label="${escapeHtml(a.label)}" data-dec-kind="${a.kind}">${escapeHtml(a.label)}</button>`).join("")}
      </div>
    </div>`;
}

function wireDecisions() {
  document.querySelectorAll<HTMLButtonElement>(".dec-btn").forEach(btn => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const id = btn.dataset.decId!;
      const label = btn.dataset.decLabel!;
      const kind = btn.dataset.decKind!;
      const result: DecisionResult = kind === "primary" ? "approved" : kind === "edit" ? "edited" : "deferred";
      const store = loadDecisions();
      const at = new Date().toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
      store[id] = { result, label, at };
      saveDecisions(store);

      // Per dom_live=false: surface to Monique review (preview = toast only, real = bot_messages)
      toast(`${label} — sent to Monique for review · ${at}`, result === "approved" ? "ok" : result === "edited" ? "warn" : "");

      // Repaint the section in place
      const section = document.getElementById("decisions-grid")?.parentElement;
      if (section) {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = renderDecisions();
        section.outerHTML = wrapper.innerHTML;
        wireDecisions();
        attachDragHandlers();
      }
    });
  });

  document.getElementById("reset-decisions")?.addEventListener("click", () => {
    saveDecisions({});
    toast("Decisions reset.", "");
    const section = document.getElementById("decisions-grid")?.parentElement;
    if (section) {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = renderDecisions();
      section.outerHTML = wrapper.innerHTML;
      wireDecisions();
      attachDragHandlers();
    }
  });
}

// ── PEPTIDE PROTOCOL CARD (editable) ────────────────────────────────────
type PeptideDoseEdit = {
  id: string;
  time: string;
  peptide: string;
  dose: string;
  route: string;
  dow: boolean[];  // 7 booleans, Mon..Sun
};
type PeptideState = {
  cycleName: string;
  totalDays: number;
  daysCompleted: number;
  startedOn: string;
  notes: string;
  doses: PeptideDoseEdit[];
  doneToday: Record<string, boolean>;
  editing: boolean;
};
const PEPTIDE_STORAGE = "alfred.peptide.v2";

function defaultPeptideState(): PeptideState {
  return {
    cycleName: PEPTIDE_CYCLE.name,
    totalDays: PEPTIDE_CYCLE.totalDays,
    daysCompleted: PEPTIDE_CYCLE.daysCompleted,
    startedOn: PEPTIDE_CYCLE.startedOn,
    notes: PEPTIDE_CYCLE.notes,
    doses: PEPTIDE_TODAY.map((d, i) => ({
      id: "pep-" + i,
      time: d.time,
      peptide: d.peptide,
      dose: d.dose,
      route: d.route,
      dow: [true, true, true, true, true, true, true], // default every day
    })),
    doneToday: {},
    editing: false,
  };
}
let peptide: PeptideState = (() => {
  const saved = loadJson<PeptideState | null>(PEPTIDE_STORAGE, null);
  return (saved && saved.doses) ? saved : defaultPeptideState();
})();
function savePeptide() { saveJson(PEPTIDE_STORAGE, peptide); }

const DOW_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

function renderPeptide(): string {
  const pct = Math.round((peptide.daysCompleted / Math.max(1, peptide.totalDays)) * 100);
  const todayDow = (new Date().getDay() + 6) % 7; // Mon=0..Sun=6
  const activeToday = peptide.doses.filter(d => d.dow[todayDow]);
  return `
    <div class="card" id="card-peptide">
      <div class="card-head">
        <div class="card-title">${ICONS.pill} Peptide protocol</div>
        <div style="display:flex;gap:8px;align-items:center;">
          <div class="card-count">Day ${peptide.daysCompleted}/${peptide.totalDays}</div>
          <button class="peptide-edit-toggle ${peptide.editing ? "active" : ""}" id="pep-edit-toggle">
            ${peptide.editing ? "Done" : "Edit"}
          </button>
        </div>
      </div>

      ${peptide.editing ? renderPeptideEditor() : renderPeptideViewer(activeToday, pct)}
    </div>`;
}

function renderPeptideViewer(active: PeptideDoseEdit[], pct: number): string {
  return `
    <div class="peptide-head">
      <div class="peptide-name">${escapeHtml(peptide.cycleName)}</div>
      <div class="peptide-cycle">Started ${escapeHtml(peptide.startedOn)}</div>
    </div>
    <div class="peptide-progress">
      <div class="peptide-bar"><div class="peptide-bar-fill" style="width:${pct}%;"></div></div>
      <div class="peptide-pct">${pct}% · ${Math.max(0, peptide.totalDays - peptide.daysCompleted)}d left</div>
    </div>
    <div>
      ${active.length === 0 ? `<div class="peptide-notes" style="font-style:normal;">No doses scheduled today. Tap Edit to set the weekly protocol.</div>` :
        active.sort((a,b)=>a.time.localeCompare(b.time)).map(d => `
          <div class="peptide-row ${peptide.doneToday[d.id] ? "done" : ""}" data-pid="${d.id}">
            <div class="peptide-tick">${ICONS.check}</div>
            <div class="peptide-time">${escapeHtml(d.time)}</div>
            <div>
              <div class="peptide-pep">${escapeHtml(d.peptide)}</div>
              <div class="peptide-route">${escapeHtml(d.route)}</div>
            </div>
            <div class="peptide-dose">${escapeHtml(d.dose)}</div>
          </div>`).join("")}
    </div>
    <div class="peptide-week">
      ${PEPTIDE_WEEK.map(d => {
        const cls = d.compliance === 100 ? "" : d.compliance >= 70 ? "partial" : "missed";
        return `
          <div class="peptide-day" title="${d.day} · ${d.compliance}%">
            <div class="peptide-day-label">${d.label}</div>
            <div class="peptide-day-dot ${cls}"></div>
          </div>`;
      }).join("")}
    </div>
    <div class="peptide-notes">${escapeHtml(peptide.notes)}</div>
  `;
}

function renderPeptideEditor(): string {
  return `
    <div class="pep-cycle-edit">
      <input class="pep-name-input" data-cycle="cycleName" placeholder="Protocol name"
             value="${escapeHtml(peptide.cycleName)}" />
      <input class="pep-days-input" data-cycle="totalDays" type="number" min="1" max="365"
             value="${peptide.totalDays}" title="Total days" />
      <input class="pep-days-input" data-cycle="daysCompleted" type="number" min="0"
             value="${peptide.daysCompleted}" title="Days completed" />
    </div>
    <div style="font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:var(--slate);font-weight:600;margin:8px 0 4px;">
      Doses · time / peptide / dose / route / days of week
    </div>
    <div id="pep-edit-rows">
      ${peptide.doses.map(d => renderPeptideEditRow(d)).join("")}
    </div>
    <button class="pep-add-btn" id="pep-add-btn">+ Add dose</button>
    <input class="pep-name-input" data-cycle="notes" placeholder="Notes (stack, refrigeration, last reconstitution)"
           value="${escapeHtml(peptide.notes)}" style="width:100%;margin-top:10px;" />
  `;
}

function renderPeptideEditRow(d: PeptideDoseEdit): string {
  return `
    <div class="pep-edit-row" data-pid="${d.id}">
      <input data-field="time" type="time" value="${escapeHtml(d.time)}" />
      <input data-field="peptide" placeholder="Peptide" value="${escapeHtml(d.peptide)}" />
      <input data-field="dose" placeholder="Dose" value="${escapeHtml(d.dose)}" />
      <input data-field="route" placeholder="Route" value="${escapeHtml(d.route)}" />
      <div class="pep-dow" style="grid-column: 1 / -1; margin-top:4px;">
        ${d.dow.map((on, i) =>
          `<button class="dow-toggle ${on ? "on" : ""}" data-dow="${i}">${DOW_LABELS[i]}</button>`).join("")}
        <button class="pep-row-del" title="Delete dose" data-action="del-pep">×</button>
      </div>
    </div>`;
}

function repaintPeptide() {
  const el = document.getElementById("card-peptide");
  if (!el) return;
  el.outerHTML = renderPeptide();
  wirePeptide();
}

function wirePeptide() {
  const card = document.getElementById("card-peptide");
  if (!card) return;

  card.querySelector("#pep-edit-toggle")?.addEventListener("click", () => {
    peptide.editing = !peptide.editing;
    savePeptide();
    repaintPeptide();
  });

  if (!peptide.editing) {
    // Tick a dose row
    card.querySelectorAll<HTMLDivElement>(".peptide-row").forEach(row => {
      row.addEventListener("click", () => {
        const id = row.dataset.pid!;
        peptide.doneToday[id] = !peptide.doneToday[id];
        savePeptide();
        row.classList.toggle("done", peptide.doneToday[id]);
      });
    });
    return;
  }

  // Cycle metadata inputs
  card.querySelectorAll<HTMLInputElement>("[data-cycle]").forEach(input => {
    input.addEventListener("change", () => {
      const key = input.dataset.cycle as keyof PeptideState;
      const v = input.type === "number" ? parseInt(input.value, 10) || 0 : input.value;
      (peptide as any)[key] = v;
      savePeptide();
    });
  });

  // Dose row edits
  card.querySelectorAll<HTMLDivElement>(".pep-edit-row").forEach(row => {
    const id = row.dataset.pid!;
    const d = peptide.doses.find(x => x.id === id);
    if (!d) return;
    row.querySelectorAll<HTMLInputElement>("[data-field]").forEach(input => {
      input.addEventListener("change", () => {
        (d as any)[input.dataset.field!] = input.value;
        savePeptide();
      });
    });
    row.querySelectorAll<HTMLButtonElement>(".dow-toggle").forEach(btn => {
      btn.addEventListener("click", () => {
        const i = parseInt(btn.dataset.dow!, 10);
        d.dow[i] = !d.dow[i];
        btn.classList.toggle("on", d.dow[i]);
        savePeptide();
      });
    });
    row.querySelector('[data-action="del-pep"]')?.addEventListener("click", () => {
      peptide.doses = peptide.doses.filter(x => x.id !== id);
      savePeptide();
      repaintPeptide();
    });
  });

  // Add dose
  card.querySelector("#pep-add-btn")?.addEventListener("click", () => {
    peptide.doses.push({
      id: "pep-" + Date.now(),
      time: "08:00",
      peptide: "",
      dose: "",
      route: "",
      dow: [true, true, true, true, true, true, true],
    });
    savePeptide();
    repaintPeptide();
  });
}

// ── PATIENTS TODAY ──────────────────────────────────────────────────────
function renderPatientsToday(): string {
  const sorted = [...PATIENTS_TODAY].sort((a, b) => a.time.localeCompare(b.time));
  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">${ICONS.patient} Patients today</div>
        <div class="card-count">${PATIENTS_TODAY.length}</div>
      </div>
      <div>
        ${sorted.map(p => `
          <div class="patient-row ${p.chip === "done" ? "done" : p.chip === "now" ? "now" : ""}">
            <div class="patient-time">${escapeHtml(p.time)}</div>
            <div class="patient-id">${escapeHtml(p.initials)}</div>
            <div>
              <div class="patient-note">${escapeHtml(p.note)}</div>
              <div class="patient-mode">${escapeHtml(p.mode)}</div>
            </div>
            <div class="patient-chip ${p.chip}">${escapeHtml(p.chip)}</div>
          </div>`).join("")}
      </div>
    </div>`;
}

// ── Live integrations strip ──────────────────────────────────────────────
function renderLive(): string {
  return `
    <div class="live-strip">
      ${LIVE.map(t => {
        const cls = t.brandClass || "";
        const href = t.link ? `href="${t.link}" target="_blank" rel="noopener"` : `href="#"`;
        const bullets = t.bullets ? `
          <div class="live-bullets">
            ${t.bullets.map(b => `
              <div class="live-bullet">
                <div class="live-bullet-label">${escapeHtml(b.label)}</div>
                <div class="live-bullet-value">${escapeHtml(b.value)}</div>
              </div>
            `).join("")}
          </div>` : "";
        return `
          <a class="live-tile" ${href}>
            <div class="live-head">
              <div class="live-brand ${cls}">${escapeHtml(t.brand)}</div>
              <div class="live-eyebrow">${escapeHtml(t.eyebrow)}</div>
            </div>
            <div class="live-primary">${escapeHtml(t.primary)}</div>
            <div class="live-sub">${escapeHtml(t.sub)}</div>
            ${bullets}
          </a>`;
      }).join("")}
    </div>`;
}

// ── Day at a glance ──────────────────────────────────────────────────────
function renderGlance(): string {
  const band = (name: string, items: { time: string; title: string; now?: boolean }[]) => `
    <div class="glance-band">
      <div class="glance-band-head">
        <div class="glance-band-name">${name}</div>
        <div class="glance-band-count">${items.length}</div>
      </div>
      ${items.map(it => `
        <div class="glance-row ${it.now ? "now" : ""}">
          <div class="glance-row-time">${escapeHtml(it.time)}</div>
          <div class="glance-row-title">${escapeHtml(it.title)}</div>
        </div>`).join("")}
    </div>`;
  return `
    <div class="glance">
      <div class="glance-lead">
        <div class="glance-lead-eyebrow">${escapeHtml(GLANCE.lead.eyebrow)}</div>
        <div class="glance-lead-title">Three patients, one <em>vendor</em>, one sign-off.</div>
        <div class="glance-lead-sub">${escapeHtml(GLANCE.lead.sub)}</div>
      </div>
      ${band("Morning", GLANCE.morning)}
      ${band("Midday", GLANCE.midday)}
      ${band("Evening", GLANCE.evening)}
    </div>`;
}

// ── CEO snapshot KPIs ───────────────────────────────────────────────────
function renderKpis(): string {
  return `<div class="kpis">
    ${KPIS.map(k => {
      const arrow = k.dir === "up" ? ICONS.arrowUp : (k.dir === "down" ? ICONS.arrowDown : "");
      const spark = k.spark === "down" ? ICONS.sparklineDown : ICONS.sparkline;
      const trendCls = k.dir === "up" ? "up" : k.dir === "down" ? "down" : "flat";
      return `<div class="kpi">
        <div class="kpi-label">${k.label}</div>
        <div class="kpi-value">${k.value}<span class="unit">${k.unit}</span></div>
        <div class="kpi-trend ${trendCls}">${arrow}<span>${k.trend}</span></div>
        <div class="kpi-spark">${spark}</div>
      </div>`;
    }).join("")}
  </div>`;
}

// ── Hero "Today's three" ────────────────────────────────────────────────
function renderHero(): string {
  return `
    <div class="section-head">
      <div>
        <div class="section-title">Today's <em>three</em></div>
        <div class="section-sub">priority focus · ${todayStr()}</div>
      </div>
      <a class="section-link" href="#">Full plan ${ICONS.arrowRight}</a>
    </div>
    <div class="hero">
      ${HERO.map(h => `
        <div class="hero-card ${h.urgent ? "urgent" : ""}">
          <div class="hero-card-time">${escapeHtml(h.time)}</div>
          <div class="hero-card-title">${escapeHtml(h.title)}</div>
          <div class="hero-card-meta">${escapeHtml(h.meta)}</div>
          <div class="hero-card-foot">
            ${h.chips.map(c => `<span class="chip ${c.kind || ""}">${escapeHtml(c.text)}</span>`).join("")}
          </div>
        </div>`).join("")}
    </div>`;
}

// ── Reminders + Apple Notes (tabbed card with full CRUD) ────────────────
function renderRemindersNotes(_unused?: unknown): string {
  const activeTab = localStorage.getItem(STORAGE_RNTAB) || "reminders";
  const isActive = (t: string) => activeTab === t ? "active" : "";
  const rems = crudState.reminders;
  const todos = crudState.todos;
  const notes = crudState.notes;
  const remainingTodos = todos.filter(t => !t.done).length;
  const remainingRems = rems.filter(r => !r.done).length;
  return `
    <div class="card" id="card-rems-notes">
      <div class="card-head">
        <div class="card-title">${ICONS.reminders} Reminders &amp; notes</div>
        <div class="card-tabs">
          <button class="card-tab ${isActive("reminders")}" data-cardtab="reminders">Reminders · ${remainingRems}</button>
          <button class="card-tab ${isActive("todos")}" data-cardtab="todos">To-dos · ${remainingTodos}</button>
          <button class="card-tab ${isActive("notes")}" data-cardtab="notes">Notes · ${notes.length}</button>
        </div>
      </div>

      <div class="card-pane ${isActive("reminders")}" data-cardpane="reminders">
        ${rems.map(r => `
          <div class="rem-row ${r.done ? "done" : ""}" data-rid="${r.id}">
            <div class="rem-tick" data-action="tick-rem">${ICONS.check}</div>
            <div class="rem-body">
              <div class="rem-text">${escapeHtml(r.text)}</div>
              <div class="rem-meta">
                <span class="rem-list">${escapeHtml(r.list)}</span>
                ${r.due ? `<span>· ${escapeHtml(r.due)}</span>` : ""}
              </div>
            </div>
            <div class="rem-action-slot">
              <button class="row-del" data-action="del-rem" title="Delete">×</button>
            </div>
          </div>`).join("")}
        <form class="add-row" data-form="add-rem">
          <input type="text" placeholder="Add a reminder..." required maxlength="120" data-add="text" />
          <select data-add="list">
            <option>Biogenix</option><option>Personal</option><option>Family</option>
          </select>
          <input type="text" placeholder="When" maxlength="20" data-add="due" />
          <button type="submit">Add</button>
        </form>
        <div class="rem-meta" style="padding-top:6px;color:var(--slate-faint);">
          Synced from Apple Reminders · last 30s ago
        </div>
      </div>

      <div class="card-pane ${isActive("todos")}" data-cardpane="todos">
        <div class="todos">
          ${todos.map(renderTodoRow).join("")}
        </div>
        <form class="add-row" data-form="add-todo">
          <input type="text" placeholder="Add a to-do..." required maxlength="120" data-add="text" />
          <input type="text" placeholder="Due" maxlength="20" data-add="due" />
          <input type="text" placeholder="Source" maxlength="20" data-add="source" />
          <button type="submit">Add</button>
        </form>
      </div>

      <div class="card-pane ${isActive("notes")}" data-cardpane="notes">
        <div class="notes-grid">
          ${notes.map(n => `
            <div class="note-row" data-nid="${n.id}">
              <div class="note-head">
                <span class="note-folder">${escapeHtml(n.folder)}</span>
                <span class="note-time">${escapeHtml(n.updated)}</span>
                <button class="row-del" data-action="del-note" title="Delete" style="margin-left:6px;">×</button>
              </div>
              <div class="note-title">${escapeHtml(n.title)}</div>
              <div class="note-preview">${escapeHtml(n.preview)}</div>
            </div>`).join("")}
        </div>
        <form class="add-row" data-form="add-note">
          <select data-add="folder">
            <option>Biogenix</option><option>Personal</option><option>Clinic</option><option>Family</option>
          </select>
          <input type="text" placeholder="Note title" required maxlength="120" data-add="title" />
          <button type="submit">Add</button>
        </form>
        <div class="rem-meta" style="padding-top:6px;color:var(--slate-faint);">
          Synced from Apple Notes (Biogenix + Personal folders)
        </div>
      </div>
    </div>`;
}

function renderTodoRow(t: Todo): string {
  return `
    <div class="todo ${t.done ? "done" : ""}" data-id="${t.id}">
      <div class="todo-check" data-action="tick-todo">${ICONS.check}</div>
      <div class="todo-body">
        <div class="todo-text">${escapeHtml(t.text)}</div>
        <div class="todo-meta">
          ${t.due ? `<span>${escapeHtml(t.due)}</span>` : ""}
          ${t.due && t.source ? `<span class="dot"></span>` : ""}
          ${t.source ? `<span>${escapeHtml(t.source)}</span>` : ""}
        </div>
      </div>
      <div class="todo-action-slot">
        <button class="row-del" data-action="del-todo" title="Delete">×</button>
      </div>
    </div>`;
}

function repaintRemsNotes() {
  const el = document.getElementById("card-rems-notes");
  if (!el) return;
  el.outerHTML = renderRemindersNotes();
  wireRemsNotes();
}

function wireRemsNotes() {
  const card = document.getElementById("card-rems-notes");
  if (!card) return;

  // Tabs (with persist)
  card.querySelectorAll<HTMLButtonElement>(".card-tab").forEach(btn => {
    btn.addEventListener("click", () => {
      const key = btn.dataset.cardtab!;
      localStorage.setItem(STORAGE_RNTAB, key);
      card.querySelectorAll(".card-tab").forEach(b => b.classList.toggle("active", b === btn));
      card.querySelectorAll<HTMLDivElement>(".card-pane").forEach(p => {
        p.classList.toggle("active", p.dataset.cardpane === key);
      });
    });
  });

  // Reminders: tick + delete
  card.querySelectorAll<HTMLDivElement>(".rem-row").forEach(row => {
    const id = row.dataset.rid!;
    row.querySelector('[data-action="tick-rem"]')?.addEventListener("click", (e) => {
      e.stopPropagation();
      const r = crudState.reminders.find(x => x.id === id);
      if (!r) return;
      r.done = !r.done;
      saveCrud();
      repaintRemsNotes();
    });
    row.querySelector('[data-action="del-rem"]')?.addEventListener("click", (e) => {
      e.stopPropagation();
      crudState.reminders = crudState.reminders.filter(x => x.id !== id);
      saveCrud();
      repaintRemsNotes();
    });
  });

  // Todos: tick + delete
  card.querySelectorAll<HTMLDivElement>(".todo").forEach(row => {
    const id = row.dataset.id!;
    row.querySelector('[data-action="tick-todo"]')?.addEventListener("click", (e) => {
      e.stopPropagation();
      const t = crudState.todos.find(x => x.id === id);
      if (!t) return;
      t.done = !t.done;
      saveCrud();
      repaintRemsNotes();
    });
    row.querySelector('[data-action="del-todo"]')?.addEventListener("click", (e) => {
      e.stopPropagation();
      crudState.todos = crudState.todos.filter(x => x.id !== id);
      saveCrud();
      repaintRemsNotes();
    });
  });

  // Notes: delete
  card.querySelectorAll<HTMLDivElement>(".note-row").forEach(row => {
    const id = row.dataset.nid!;
    row.querySelector('[data-action="del-note"]')?.addEventListener("click", (e) => {
      e.stopPropagation();
      crudState.notes = crudState.notes.filter(x => x.id !== id);
      saveCrud();
      repaintRemsNotes();
    });
  });

  // Add forms
  card.querySelector<HTMLFormElement>('[data-form="add-rem"]')?.addEventListener("submit", (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data = formData(form);
    if (!data.text) return;
    crudState.reminders.push({
      id: "r" + Date.now(),
      list: data.list || "Biogenix",
      text: data.text,
      due: data.due || undefined,
      done: false,
    });
    saveCrud();
    repaintRemsNotes();
  });

  card.querySelector<HTMLFormElement>('[data-form="add-todo"]')?.addEventListener("submit", (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data = formData(form);
    if (!data.text) return;
    crudState.todos.push({
      id: "t" + Date.now(),
      text: data.text,
      due: data.due || undefined,
      source: data.source || undefined,
      done: false,
    });
    saveCrud();
    repaintRemsNotes();
  });

  card.querySelector<HTMLFormElement>('[data-form="add-note"]')?.addEventListener("submit", (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data = formData(form);
    if (!data.title) return;
    crudState.notes.unshift({
      id: "n" + Date.now(),
      folder: data.folder || "Biogenix",
      title: data.title,
      preview: "(new note · click to write)",
      updated: "Just now",
    });
    saveCrud();
    repaintRemsNotes();
  });
}

function formData(form: HTMLFormElement): Record<string, string> {
  const out: Record<string, string> = {};
  form.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-add]").forEach(el => {
    out[el.dataset.add!] = el.value.trim();
  });
  return out;
}

function renderTodo(t: Todo): string {
  return `
    <div class="todo ${t.done ? "done" : ""}" data-id="${t.id}">
      <div class="todo-check">${ICONS.check}</div>
      <div class="todo-body">
        <div class="todo-text">${escapeHtml(t.text)}</div>
        <div class="todo-meta">
          ${t.due ? `<span>${escapeHtml(t.due)}</span>` : ""}
          ${t.due && t.source ? `<span class="dot"></span>` : ""}
          ${t.source ? `<span>${escapeHtml(t.source)}</span>` : ""}
        </div>
      </div>
    </div>`;
}

// ── Schedule (morgen.so time-block) ─────────────────────────────────────
const PX_PER_HOUR = 48;
function parseHM(s: string): number {
  const [h, m] = s.split(":").map(Number);
  return h + (m || 0) / 60;
}
function topPx(hm: string): number { return (parseHM(hm) - DAY_START_HOUR) * PX_PER_HOUR; }
function heightPx(start: string, end: string): number {
  return Math.max(26, (parseHM(end) - parseHM(start)) * PX_PER_HOUR - 3);
}
function nowDecimal(): number {
  const d = new Date();
  return d.getHours() + d.getMinutes() / 60;
}
function renderBlock(s: ScheduleItem): string {
  return `
    <div class="block ${s.kind} ${s.now ? "now" : ""}"
         style="top:${topPx(s.start)}px;height:${heightPx(s.start, s.end)}px;">
      <div class="block-time">${escapeHtml(s.start)} – ${escapeHtml(s.end)}</div>
      <div class="block-title">${escapeHtml(s.title)}</div>
      <div class="block-meta">${escapeHtml(s.meta)}</div>
    </div>`;
}
function renderBlockInteractive(s: ScheduleItem, idx: number): string {
  return `
    <div class="block ${s.kind} ${s.now ? "now" : ""}"
         style="top:${topPx(s.start)}px;height:${heightPx(s.start, s.end)}px;"
         data-block-idx="${idx}">
      <div class="block-time">${escapeHtml(s.start)} – ${escapeHtml(s.end)}</div>
      <div class="block-title">${escapeHtml(s.title)}</div>
      <div class="block-meta">${escapeHtml(s.meta)}</div>
    </div>`;
}

function renderSchedule(): string {
  const hours: string[] = [];
  for (let h = DAY_START_HOUR; h < DAY_END_HOUR; h++) {
    const label = h < 12 ? `${h} AM` : (h === 12 ? "12 PM" : `${h - 12} PM`);
    hours.push(label);
  }
  const totalH = (DAY_END_HOUR - DAY_START_HOUR) * PX_PER_HOUR;
  const nowDec = nowDecimal();
  const showNow = nowDec >= DAY_START_HOUR && nowDec <= DAY_END_HOUR;
  const nowTop = showNow ? (nowDec - DAY_START_HOUR) * PX_PER_HOUR + 8 : -1000;
  const nowLabel = new Date().toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `
    <div class="card" id="card-schedule">
      <div class="card-head">
        <div class="card-title">${ICONS.calendar} Today's schedule</div>
        <a class="section-link" href="#">Week ${ICONS.arrowRight}</a>
      </div>
      <div class="daygrid-wrap">
        <div class="daygrid" style="height:${totalH + 16}px;">
          <div class="daygrid-hours">
            ${hours.map(h => `<div class="daygrid-hour" style="height:${PX_PER_HOUR}px;">${h}</div>`).join("")}
          </div>
          <div class="daygrid-board">
            ${SCHEDULE.map(renderBlockInteractive).join("")}
            ${showNow ? `<div class="daygrid-now" data-time="${nowLabel}" style="top:${nowTop}px;"></div>` : ""}
          </div>
        </div>
        <div class="schedule-detail" id="schedule-detail">
          <button class="sd-close" id="schedule-detail-close">×</button>
          <div id="schedule-detail-body"></div>
        </div>
      </div>
    </div>`;
}

function wireSchedule() {
  document.querySelectorAll<HTMLDivElement>("#card-schedule .block").forEach(b => {
    b.addEventListener("click", () => {
      const idx = parseInt(b.dataset.blockIdx || "-1", 10);
      const s = SCHEDULE[idx];
      if (!s) return;
      const panel = document.getElementById("schedule-detail");
      const body = document.getElementById("schedule-detail-body");
      if (!panel || !body) return;
      const kindLabel = s.kind.charAt(0).toUpperCase() + s.kind.slice(1);
      body.innerHTML = `
        <div class="sd-eyebrow">${escapeHtml(kindLabel)} block</div>
        <div class="sd-title">${escapeHtml(s.title)}</div>
        <div class="sd-time">${escapeHtml(s.start)} – ${escapeHtml(s.end)} · ${escapeHtml(s.meta)}</div>
        <div class="sd-section">Notes</div>
        <div class="sd-meta">Halaxy file open. Coviu room provisioned. Last-visit summary ready on tap.</div>
        <div class="sd-section">Quick actions</div>
        <div class="sd-actions">
          <button class="sd-action primary">Open primary tool</button>
          <button class="sd-action">Brief me in 60 seconds</button>
          <button class="sd-action">Reschedule</button>
          <button class="sd-action">Add prep note</button>
        </div>
        <div class="sd-section">Attendees</div>
        <div class="sd-meta">${s.kind === "patient" ? "Patient (de-identified) · Dr Schaffner" : s.kind === "vendor" ? "K. Mizoguchi (Terumo) · Dr Schaffner · Anna" : "Dr Schaffner · Team"}</div>
      `;
      panel.classList.add("open");
    });
  });
  document.getElementById("schedule-detail-close")?.addEventListener("click", () => {
    document.getElementById("schedule-detail")?.classList.remove("open");
  });
  // Esc to close
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") document.getElementById("schedule-detail")?.classList.remove("open");
  });
}

// ── Inbox triage ────────────────────────────────────────────────────────
function renderInboxTriage(): string {
  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">${ICONS.inbox} Inbox triage</div>
        <div class="card-count">47 unread</div>
      </div>
      <div class="triage">
        ${TRIAGE.map(t => `
          <div class="triage-row">
            <div class="triage-left">
              <div class="triage-avatar">${escapeHtml(t.initials)}</div>
              <div class="triage-meta">
                <div class="triage-name">${escapeHtml(t.name)}</div>
                <div class="triage-sub">${escapeHtml(t.sub)}</div>
              </div>
            </div>
            <div class="triage-count ${t.flavour === "red" ? "red" : t.flavour === "amber" ? "amber" : ""}">${t.count}</div>
          </div>`).join("")}
      </div>
    </div>`;
}

// ── Pipeline ────────────────────────────────────────────────────────────
function renderPipeline(): string {
  const total = PIPELINE.reduce((n, c) => n + c.items.length, 0);
  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">${ICONS.patient} Patient pipeline</div>
        <div class="card-count">${total} active</div>
      </div>
      <div class="pipeline">
        ${PIPELINE.map(col => `
          <div class="pipe-col">
            <div class="pipe-col-head">
              <div class="pipe-col-name">${col.col}</div>
              <div class="pipe-col-count">${col.items.length}</div>
            </div>
            ${col.items.map(it => `
              <div class="pipe-card">
                <div class="pipe-name">${escapeHtml(it.name)}</div>
                <div class="pipe-sub">${escapeHtml(it.sub)}</div>
              </div>`).join("")}
          </div>`).join("")}
      </div>
    </div>`;
}

// ── Regulatory ──────────────────────────────────────────────────────────
function renderRegulatory(): string {
  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">${ICONS.alert} Regulatory feed</div>
        <a class="section-link" href="#">All ${ICONS.arrowRight}</a>
      </div>
      <div class="feed">
        ${REGULATORY.map(r => `
          <div class="feed-item">
            <div class="feed-head">
              <span class="feed-source">${escapeHtml(r.source)}</span>
              <span class="feed-date">${escapeHtml(r.date)}</span>
            </div>
            <div class="feed-title">${escapeHtml(r.title)}</div>
          </div>`).join("")}
      </div>
    </div>`;
}

// ── Investments / portfolio / news ──────────────────────────────────────
function renderInvestments(): string {
  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">${ICONS.chart} Investments &amp; portfolio</div>
        <a class="section-link" href="#">Open ${ICONS.arrowRight}</a>
      </div>
      <div class="portfolio-head">
        <div>
          <div class="port-total">${escapeHtml(PORTFOLIO_SUMMARY.total)}</div>
          <span class="port-chg ${PORTFOLIO_SUMMARY.changeDir}">${escapeHtml(PORTFOLIO_SUMMARY.changeAud)} · ${escapeHtml(PORTFOLIO_SUMMARY.changePct)} today</span>
        </div>
        <div class="port-asof">${escapeHtml(PORTFOLIO_SUMMARY.asOf)}</div>
      </div>
      <div class="holdings">
        ${HOLDINGS.map(h => `
          <div class="hold-row">
            <div>
              <div class="hold-id">${escapeHtml(h.ticker)}</div>
              <div class="hold-name">${escapeHtml(h.name)} · ${escapeHtml(h.alloc)}</div>
            </div>
            <div class="hold-val">${escapeHtml(h.value)}</div>
            <div class="hold-chg ${h.dir === "down" ? "down" : "up"}">${escapeHtml(h.chgPct)}</div>
          </div>`).join("")}
      </div>
    </div>`;
}

function renderInvestNews(): string {
  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">${ICONS.cloud} Markets &amp; sector news</div>
        <a class="section-link" href="#">All ${ICONS.arrowRight}</a>
      </div>
      <div class="news">
        ${INVEST_NEWS.map(n => `
          <div class="news-item">
            <div class="news-meta">
              <span class="news-tag ${n.tag}">${escapeHtml(n.tag)}</span>
              <span class="news-source">${escapeHtml(n.source)}</span>
              <span class="news-date">${escapeHtml(n.date)}</span>
            </div>
            <div class="news-title">${escapeHtml(n.title)}</div>
          </div>`).join("")}
      </div>
    </div>`;
}

// ── Personal cards ──────────────────────────────────────────────────────
function renderPersonal(): string {
  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">${ICONS.users} Personal &amp; family</div>
      </div>
      <div class="personal-grid">
        ${PERSONAL.map(p => `
          <div class="personal-card">
            <div class="personal-eyebrow">${escapeHtml(p.eyebrow)}</div>
            <div class="personal-primary">${escapeHtml(p.primary)}</div>
            <div class="personal-sub">${escapeHtml(p.sub)}</div>
          </div>`).join("")}
      </div>
    </div>`;
}

// ── Staff pulse ─────────────────────────────────────────────────────────
function renderStaff(): string {
  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">${ICONS.users} Team today</div>
        <div class="card-count">${STAFF_PULSE.length} on</div>
      </div>
      <div>
        ${STAFF_PULSE.map(s => `
          <div class="staff-row">
            <div class="staff-left">
              <span class="staff-name">${escapeHtml(s.name)}</span>
              <span class="staff-role">${escapeHtml(s.role)}</span>
            </div>
            <span class="staff-status">${escapeHtml(s.status)}</span>
          </div>`).join("")}
      </div>
    </div>`;
}

// ── View tabs ───────────────────────────────────────────────────────────
function renderViewTabs(active: View): string {
  const tabs: { key: View; label: string; count?: number }[] = [
    { key: "today",       label: "Today" },
    { key: "biogenix",    label: "Biogenix",    count: 12 },
    { key: "personal",    label: "Personal" },
    { key: "investments", label: "Investments", count: HOLDINGS.length },
    { key: "patients",    label: "Patients",    count: 247 },
    { key: "inbox",       label: "Inbox",       count: 47 },
  ];
  return `<div class="viewtabs">
    ${tabs.map(t => `
      <button class="viewtab ${t.key === active ? "active" : ""}" data-view="${t.key}">
        ${escapeHtml(t.label)}
        ${t.count != null ? `<span class="viewtab-count">${t.count}</span>` : ""}
      </button>`).join("")}
  </div>`;
}

// ── Canvas content per view ─────────────────────────────────────────────
function renderCanvas(view: View): string {
  switch (view) {
    case "today":
      // CEO-first layout: headline → next-up → decisions → schedule+side modules
      return `
        ${renderHeadline()}
        ${renderNextUp()}
        ${renderDecisions()}
        <div class="split">
          <div class="split-col">
            ${renderSchedule()}
          </div>
          <div class="split-col">
            ${renderPatientsToday()}
            ${renderInboxTriageCompact()}
            ${renderAwaitingCompact()}
          </div>
        </div>
        <div class="split">
          <div class="split-col">
            ${renderRemindersNotes()}
          </div>
          <div class="split-col">
            ${renderPeptide()}
            ${renderStaff()}
          </div>
        </div>
        <div class="split">
          <div class="split-col">${renderPersonal()}</div>
          <div class="split-col">${renderRegulatory()}</div>
        </div>
        ${renderLiveScroll()}
        <div class="split">
          <div class="split-col">${renderRegulatory()}</div>
          <div class="split-col">${renderInvestments()}</div>
        </div>
      `;
    case "biogenix":
      return `
        ${renderHeadline()}
        ${renderDecisions()}
        <div class="split">
          <div class="split-col">${renderSchedule()}</div>
          <div class="split-col">
            ${renderPatientsToday()}
            ${renderInboxTriageCompact()}
            ${renderStaff()}
          </div>
        </div>
        <div class="split">
          <div class="split-col">${renderPipeline()}</div>
          <div class="split-col">${renderRegulatory()}</div>
        </div>
        ${renderLiveScroll()}
      `;
    case "personal":
      return `
        ${renderHeadline()}
        <div class="split">
          <div class="split-col">${renderPersonal()}</div>
          <div class="split-col">${renderRemindersNotes()}</div>
        </div>
      `;
    case "investments":
      return `
        ${renderHeadline()}
        <div class="split">
          <div class="split-col">${renderInvestments()}</div>
          <div class="split-col">${renderInvestNews()}</div>
        </div>
      `;
    case "patients":
      return `
        ${renderHeadline()}
        <div class="split">
          <div class="split-col">${renderPatientsToday()}</div>
          <div class="split-col">${renderPipeline()}</div>
        </div>`;
    case "inbox":
      return `
        ${renderHeadline()}
        <div class="split">
          <div class="split-col">${renderInboxTriage()}</div>
          <div class="split-col">${renderAwaitingCompact()}</div>
        </div>`;
    case "calendar":
      return `
        ${renderHeadline()}
        ${renderSchedule()}`;
  }
}

// Compact variants for the right column of the split layout
function renderInboxTriageCompact(): string {
  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">${ICONS.inbox} Inbox · top senders</div>
        <div class="card-count">47</div>
      </div>
      <div class="triage">
        ${TRIAGE.slice(0, 4).map(t => `
          <div class="triage-row">
            <div class="triage-left">
              <div class="triage-avatar">${escapeHtml(t.initials)}</div>
              <div class="triage-meta">
                <div class="triage-name">${escapeHtml(t.name)}</div>
                <div class="triage-sub">${escapeHtml(t.sub)}</div>
              </div>
            </div>
            <div class="triage-count ${t.flavour === "red" ? "red" : t.flavour === "amber" ? "amber" : ""}">${t.count}</div>
          </div>`).join("")}
      </div>
    </div>`;
}

function renderAwaitingCompact(): string {
  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">${ICONS.bell} Awaiting reply</div>
        <div class="card-count">${AWAITING.length}</div>
      </div>
      <div class="awaiting" style="margin:0 calc(var(--s-3) * -1);">
        ${AWAITING.slice(0, 4).map(a => `
          <div class="aw-item" style="padding:var(--s-2) var(--s-3);">
            <div class="aw-name">${escapeHtml(a.name)}</div>
            <div class="aw-sub">${escapeHtml(a.sub)}</div>
          </div>`).join("")}
      </div>
    </div>`;
}

function renderLiveScroll(): string {
  return `
    <div class="section-head">
      <div>
        <div class="section-title">Connected <em>systems</em></div>
        <div class="section-sub">live status · one tap to source</div>
      </div>
    </div>
    <div class="live-scroll">
      ${LIVE.map(t => {
        const cls = t.brandClass || "";
        const href = t.link ? `href="${t.link}" target="_blank" rel="noopener"` : `href="#"`;
        return `
          <a class="live-tile" ${href}>
            <div class="live-head">
              <div class="live-brand ${cls}">${escapeHtml(t.brand)}</div>
              <div class="live-eyebrow">${escapeHtml(t.eyebrow)}</div>
            </div>
            <div class="live-primary">${escapeHtml(t.primary)}</div>
            <div class="live-sub">${escapeHtml(t.sub)}</div>
          </a>`;
      }).join("")}
    </div>`;
}

// ── Side rail (chat history + chat) ─────────────────────────────────────
function renderRail(): string {
  return `
    <aside class="rail">
      <div class="rail-tab-strip" id="rail-expand-tab">
        <button class="rail-collapse-btn" id="rail-expand-btn" title="Open chat">${ICONS.chevronLeft}</button>
        <div class="rail-tab-strip-icon">${ICONS.chat}</div>
        <div class="rail-tab-strip-label"><span class="pulse"></span> Chat with Alfred</div>
        <div class="rail-tab-strip-badge">${MESSAGES_PREVIEW.length}</div>
      </div>

      <div class="rail-head">
        <div class="rail-title">
          <span>Conversations</span>
          <div style="display:flex;gap:6px;align-items:center;">
            <button class="new-chat" id="new-chat-btn">+ New</button>
            <button class="rail-collapse-btn" id="rail-collapse-btn" title="Collapse">${ICONS.chevronRight}</button>
          </div>
        </div>
        <div class="rail-tabs">
          <button class="rail-tab active" data-tab="history">History</button>
          <button class="rail-tab" data-tab="awaiting">Awaiting · ${AWAITING.length}</button>
        </div>
      </div>
      <div class="rail-body">
        <div class="rail-pane active" data-pane="history">${renderChatHistory()}</div>
        <div class="rail-pane" data-pane="awaiting">${renderAwaiting()}</div>
      </div>
      ${renderChatDrawer()}
    </aside>`;
}

function renderChatHistory(): string {
  return `<div class="history">
    ${CHAT_HISTORY.map(g => `
      <div class="history-day">${g.day}</div>
      ${g.items.map(it => `
        <div class="history-item ${(it as any).active ? "active" : ""}">
          <div class="history-snippet">${escapeHtml(it.snippet)}</div>
          <div class="history-time">${escapeHtml(it.time)}</div>
        </div>`).join("")}`).join("")}
  </div>`;
}

function renderAwaiting(): string {
  return `<div class="awaiting">
    ${AWAITING.map(a => `
      <div class="aw-item">
        <div class="aw-name">${escapeHtml(a.name)}</div>
        <div class="aw-sub">${escapeHtml(a.sub)}</div>
        <div class="aw-foot">
          <button class="ping">Ping</button>
          <span class="chip ghost">Snooze</span>
        </div>
      </div>`).join("")}
  </div>`;
}

function renderChatDrawer(): string {
  return `
    <div class="chat-drawer" id="chat-drawer">
      <div class="chat-drawer-head" id="chat-drawer-head">
        <div class="chat-drawer-title">
          <span class="pulse"></span>
          Chat with Alfred
        </div>
        <button class="chat-toggle">${ICONS.chevronDown}</button>
      </div>
      <div class="messages" id="messages">
        ${MESSAGES_PREVIEW.map(m => `
          <div class="message-row ${m.role}">
            <div>
              <div class="bubble">${escapeHtml(m.content)}</div>
              <div class="time">${timeAgo(m.mins)}</div>
            </div>
          </div>`).join("")}
      </div>
      <div class="input-row">
        <textarea id="chat-input" rows="1" placeholder="Ask Alfred anything..."></textarea>
        <button id="send-btn">Send</button>
      </div>
    </div>`;
}

// ── "What changed since last visit" chip ────────────────────────────────
function renderChangedChip(): string {
  const last = parseInt(localStorage.getItem(STORAGE_LASTSEEN) || "0", 10);
  if (!last) {
    localStorage.setItem(STORAGE_LASTSEEN, String(Date.now()));
    return "";
  }
  // Fictional: pretend N items are new based on time delta (max 8)
  const minsSince = Math.max(1, Math.round((Date.now() - last) / 60000));
  const newCount = Math.min(8, Math.max(0, Math.floor(minsSince / 3)));
  if (newCount < 1) return "";
  const since = new Date(last).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
  return `
    <button class="changed-chip" id="changed-chip" title="Mark all as seen">
      <span class="pulse"></span>
      ${newCount} new since ${escapeHtml(since)}
    </button>`;
}

// ── Drag-and-drop card reordering (handle-initiated, container-scoped) ──
const STORAGE_CARDORDER = "alfred.cardOrder.v1";
let dragSrc: HTMLElement | null = null;
let dragKind: "card" | "decision" | null = null;

const DRAG_HANDLE_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="6" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="9" cy="18" r="1"/><circle cx="15" cy="6" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="18" r="1"/></svg>`;

function injectDragHandles() {
  document.querySelectorAll<HTMLElement>(".card, .decision").forEach(el => {
    if (el.querySelector(":scope > .drag-handle")) return;
    const h = document.createElement("div");
    h.className = "drag-handle";
    h.innerHTML = DRAG_HANDLE_SVG;
    h.title = "Drag to reorder";
    el.appendChild(h);
    // The handle is the only drag initiator
    h.addEventListener("mousedown", () => { el.setAttribute("draggable", "true"); });
    h.addEventListener("mouseup",   () => { el.setAttribute("draggable", "false"); });
    el.setAttribute("draggable", "false");
  });
}

function attachDragHandlers() {
  injectDragHandles();

  document.querySelectorAll<HTMLElement>(".card, .decision").forEach(el => {
    el.addEventListener("dragstart", onDragStart);
    el.addEventListener("dragend", onDragEnd);
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
  });

  // Containers also accept drops (so you can drop into empty space at bottom)
  document.querySelectorAll<HTMLElement>(".split-col, #decisions-grid").forEach(col => {
    col.addEventListener("dragover", onContainerOver);
    col.addEventListener("dragleave", onContainerLeave);
    col.addEventListener("drop", onContainerDrop);
  });
}

function onDragStart(this: HTMLElement, e: DragEvent) {
  // Only start drag if dragstart originated from the drag handle
  const target = e.target as HTMLElement;
  if (!target.closest(".drag-handle")) {
    e.preventDefault();
    return;
  }
  dragSrc = this;
  dragKind = this.classList.contains("decision") ? "decision" : "card";
  this.classList.add("dragging");
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", this.id || "card");
  }
}
function onDragEnd(this: HTMLElement) {
  this.classList.remove("dragging");
  document.querySelectorAll(".drag-over").forEach(el => el.classList.remove("drag-over"));
  this.setAttribute("draggable", "false");
  dragSrc = null;
  dragKind = null;
}
function onDragOver(this: HTMLElement, e: DragEvent) {
  if (!dragSrc || dragSrc === this) return;
  // Only allow over same-kind siblings
  const sameKind = (this.classList.contains("decision") ? "decision" : "card") === dragKind;
  if (!sameKind) return;
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
  this.classList.add("drag-over");
}
function onDragLeave(this: HTMLElement) { this.classList.remove("drag-over"); }
function onDrop(this: HTMLElement, e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  this.classList.remove("drag-over");
  if (!dragSrc || dragSrc === this) return;
  const sameKind = (this.classList.contains("decision") ? "decision" : "card") === dragKind;
  if (!sameKind) return;
  const rect = this.getBoundingClientRect();
  const after = e.clientY > rect.top + rect.height / 2;
  const parent = this.parentElement;
  if (!parent) return;
  if (after) parent.insertBefore(dragSrc, this.nextSibling);
  else parent.insertBefore(dragSrc, this);
  toast("Layout saved.", "ok", 1400);
  persistLayout();
}

function onContainerOver(this: HTMLElement, e: DragEvent) {
  if (!dragSrc) return;
  // Decisions stay in decisions-grid; cards stay in split-cols
  if (dragKind === "decision" && this.id !== "decisions-grid") return;
  if (dragKind === "card" && !this.classList.contains("split-col")) return;
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
}
function onContainerLeave(this: HTMLElement) { this.classList.remove("drag-over"); }
function onContainerDrop(this: HTMLElement, e: DragEvent) {
  if (!dragSrc) return;
  if (dragKind === "decision" && this.id !== "decisions-grid") return;
  if (dragKind === "card" && !this.classList.contains("split-col")) return;
  // Only handle if the actual target is the container itself (not a child card)
  if (e.target !== this) return;
  e.preventDefault();
  this.appendChild(dragSrc);
  toast("Layout saved.", "ok", 1400);
  persistLayout();
}

function persistLayout() {
  const layout: Record<string, string[]> = {};
  document.querySelectorAll<HTMLElement>(".split-col, #decisions-grid").forEach((col, i) => {
    const key = col.dataset.colKey || col.id || ("col-" + i);
    if (!col.dataset.colKey) col.dataset.colKey = key;
    layout[key] = Array.from(col.children)
      .map(c => (c as HTMLElement).id || (c as HTMLElement).dataset.decision || "")
      .filter(Boolean);
  });
  saveJson(STORAGE_CARDORDER, layout);
}

function restoreLayout() {
  const layout = loadJson<Record<string, string[]> | null>(STORAGE_CARDORDER, null);
  if (!layout) return;
  // For each saved container, re-order children to match
  Object.entries(layout).forEach(([key, ids], i) => {
    const containers = Array.from(document.querySelectorAll<HTMLElement>(".split-col, #decisions-grid"));
    const col = containers.find(c => c.id === key) || containers[i];
    if (!col) return;
    ids.forEach(id => {
      const el = col.querySelector<HTMLElement>(`#${CSS.escape(id)}`)
              || document.querySelector<HTMLElement>(`[data-decision="${CSS.escape(id)}"]`);
      if (el && el.parentElement === col) col.appendChild(el);
    });
  });
}

// ── Nav rail with VIEW switching ────────────────────────────────────────
function navButton(view: View, title: string, icon: string, active: View): string {
  return `<button class="nav-btn ${view === active ? "active" : ""}" data-navview="${view}" data-tip="${title}" aria-label="${title}">${icon}</button>`;
}

// ── Main entry point ────────────────────────────────────────────────────
export function renderDashboard(app: HTMLElement, user: User) {
  loadCrud();
  const initial = (user.email || "D").trim().charAt(0).toUpperCase();
  const firstName = "Dominik";
  let view = (loadJson<View | null>(STORAGE_VIEW, null) || "today") as View;
  let railState = loadJson<{ collapsed: boolean }>(STORAGE_RAIL, { collapsed: false });

  function paint() {
    const shellCls = railState.collapsed ? "rail-collapsed" : "";
    app.innerHTML = `
      <div class="shell ${shellCls}" id="shell">
        <aside class="nav-rail">
          <div class="nav-logo">A</div>
          ${navButton("today",       "Today",       ICONS.home,     view)}
          ${navButton("biogenix",    "Biogenix",    ICONS.pill,     view)}
          ${navButton("personal",    "Personal",    ICONS.users,    view)}
          ${navButton("investments", "Investments", ICONS.chart,    view)}
          ${navButton("inbox",       "Inbox",       ICONS.inbox,    view)}
          ${navButton("calendar",    "Calendar",    ICONS.calendar, view)}
          ${navButton("patients",    "Patients",    ICONS.patient,  view)}
          <div class="nav-spacer"></div>
          <button class="nav-btn" data-tip="Alerts" aria-label="Alerts">${ICONS.bell}</button>
          <button class="nav-btn" data-tip="Settings" aria-label="Settings">${ICONS.settings}</button>
        </aside>

        <section class="main">
          <header class="head">
            <div class="head-left">
              <div class="head-greet">Good morning, <em>${escapeHtml(firstName)}</em>.</div>
              <div class="head-sub">${escapeHtml(todayStr())} · clinic day</div>
            </div>
            <div class="head-right">
              ${renderChangedChip()}
              <div class="weather-chip">${ICONS.sun} 19° · clear · Sydney</div>
              <div class="head-avatar">${escapeHtml(initial)}</div>
              <button class="head-signout" id="signout">Sign out</button>
            </div>
          </header>
          <div class="preview-strip">
            <span>Preview · sample content · live data activates after Monique greenlight</span>
            <span>v0.4 · cockpit</span>
          </div>
          <div class="canvas">
            ${renderCanvas(view)}
          </div>
        </section>

        ${renderRail()}
      </div>
    `;
    wireInteractions();
  }

  function wireInteractions() {
    // Sign out
    document.getElementById("signout")?.addEventListener("click", async () => {
      sessionStorage.removeItem("alfred.preview.user");
      await supabase.auth.signOut();
      location.reload();
    });

    // Nav-rail view switching
    document.querySelectorAll<HTMLButtonElement>(".nav-btn[data-navview]").forEach(btn => {
      btn.addEventListener("click", () => {
        const v = btn.dataset.navview as View;
        view = v;
        saveJson(STORAGE_VIEW, v);
        paint();
      });
    });

    // View tabs (top of canvas)
    document.querySelectorAll<HTMLButtonElement>(".viewtab").forEach(btn => {
      btn.addEventListener("click", () => {
        const v = btn.dataset.view as View;
        view = v;
        saveJson(STORAGE_VIEW, v);
        paint();
      });
    });

    // Settings/Alerts nav buttons (no-op placeholders, just active highlight)
    document.querySelectorAll<HTMLButtonElement>(".nav-btn:not([data-navview])").forEach(btn => {
      btn.addEventListener("click", () => {
        document.querySelectorAll(".nav-btn").forEach(b => b.classList.remove("active"));
        btn.classList.add("active");
      });
    });

    // Rail collapse
    document.getElementById("rail-collapse-btn")?.addEventListener("click", () => {
      railState.collapsed = true;
      saveJson(STORAGE_RAIL, railState);
      paint();
    });
    document.getElementById("rail-expand-btn")?.addEventListener("click", () => {
      railState.collapsed = false;
      saveJson(STORAGE_RAIL, railState);
      paint();
    });
    document.getElementById("rail-expand-tab")?.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).id !== "rail-expand-btn") {
        railState.collapsed = false;
        saveJson(STORAGE_RAIL, railState);
        paint();
      }
    });

    // Reminders / Todos / Notes — full CRUD
    wireRemsNotes();
    // Decisions
    wireDecisions();
    // Schedule click → detail panel
    wireSchedule();
    // Peptide ticks
    wirePeptide();
    // Drag-and-drop on all cards/decisions, then restore previous order
    attachDragHandlers();
    restoreLayout();
    // "What changed" chip → clears + sets new lastSeen
    document.getElementById("changed-chip")?.addEventListener("click", () => {
      localStorage.setItem(STORAGE_LASTSEEN, String(Date.now()));
      document.getElementById("changed-chip")?.remove();
      toast("All caught up.", "ok", 1600);
    });

    // Rail history/awaiting tabs
    document.querySelectorAll<HTMLButtonElement>(".rail-tab").forEach(btn => {
      btn.addEventListener("click", () => {
        const tab = btn.dataset.tab;
        document.querySelectorAll(".rail-tab").forEach(b => b.classList.toggle("active", b === btn));
        document.querySelectorAll<HTMLDivElement>(".rail-pane").forEach(p => {
          p.classList.toggle("active", p.dataset.pane === tab);
        });
      });
    });

    // Chat drawer collapse
    document.getElementById("chat-drawer-head")?.addEventListener("click", (e) => {
      // Don't toggle if clicking the X
      document.getElementById("chat-drawer")?.classList.toggle("collapsed");
      e.stopPropagation();
    });

    // Chat input (preview-only, echoes user message)
    const ta = document.getElementById("chat-input") as HTMLTextAreaElement | null;
    if (ta) {
      ta.addEventListener("input", () => {
        ta.style.height = "auto";
        ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
      });
      ta.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && !(e as KeyboardEvent).shiftKey) {
          e.preventDefault();
          submitChat(ta);
        }
      });
      document.getElementById("send-btn")?.addEventListener("click", () => submitChat(ta));
    }
  }

  function submitChat(ta: HTMLTextAreaElement) {
    const v = ta.value.trim();
    if (!v) return;
    const messages = document.getElementById("messages");
    if (messages) {
      messages.insertAdjacentHTML("beforeend", `
        <div class="message-row user">
          <div>
            <div class="bubble">${escapeHtml(v)}</div>
            <div class="time">${new Date().toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}</div>
          </div>
        </div>`);
      messages.scrollTop = messages.scrollHeight;
    }
    ta.value = "";
    ta.style.height = "auto";
  }

  paint();
}
